import {
  type ClientFrame,
  type HostFrame,
  parseHostFrame,
} from "../protocol/frames";
import type { KernelTransport } from "./transport";

const WORKER_NAME = "atd-market-data";

/**
 * Returns undefined when this browser cannot give us a shared worker, so the
 * caller can fall back. The `new URL(...)` argument must stay inline: Vite only
 * rewrites the worker specifier when it can see it at the construction site.
 */
export const createSharedWorkerTransport = (options?: {
  /** Called when the worker fails after construction, so the caller can fall back. */
  onFatal?: () => void;
}): KernelTransport | undefined => {
  if (typeof SharedWorker !== "function") {
    return undefined;
  }

  let worker: SharedWorker;
  try {
    worker = new SharedWorker(new URL("./sharedWorker.ts", import.meta.url), {
      type: "module",
      name: WORKER_NAME,
    });
  } catch {
    return undefined;
  }

  // Fires when the browser cannot load or parse the module worker. The
  // constructor has already returned by then, so this is the only signal.
  worker.addEventListener("error", (event) => {
    event.preventDefault();
    console.warn(
      "[atd] shared market data worker failed; using a local socket",
    );
    options?.onFatal?.();
  });

  const listeners = new Set<(frame: HostFrame) => void>();
  const onMessage = (event: MessageEvent<unknown>) => {
    const frame = parseHostFrame(event.data);
    if (frame === undefined) {
      return;
    }
    for (const listener of [...listeners]) {
      listener(frame);
    }
  };

  worker.port.addEventListener("message", onMessage);
  worker.port.start();

  const post = (frame: ClientFrame) => {
    worker.port.postMessage(frame);
  };

  const subscribe = (listener: (frame: HostFrame) => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const close = () => {
    listeners.clear();
    worker.port.removeEventListener("message", onMessage);
    worker.port.close();
  };

  return {
    post,
    subscribe,
    close,
  };
};
